import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  TextField,
  CircularProgress,
  Typography,
  IconButton,
  Paper,
  useTheme,
  Fade,
  Slide,
  alpha,
  Chip,
  Tooltip,
  Skeleton,
} from "@mui/material";
import { Close as CloseIcon, Search as SearchIcon } from "@mui/icons-material";
import { useQuery } from "urql";
import { pairsQuery } from "../support/mutations";
import { useSearchParams, useNavigate } from "react-router-dom";
import { debounce } from "@mui/material/utils";
import Avatar from "./Avatar";
import { useAtom } from "jotai";
import { recentPairsAtom } from "../atoms/recentPairs";
import { formatNumberHumanize } from "../utils/number";

const COLLAPSED_DRAWER_WIDTH = 56;

const FullScreenSearch = ({ onClose, open }) => {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [recentPairs, setRecentPairs] = useAtom(recentPairsAtom);

  const [searchResults, reexecuteSearch] = useQuery({
    query: pairsQuery,
    variables: { query: searchQuery },
    pause: !searchQuery || !open,
  });

  const debouncedSetSearchQuery = useMemo(
    () =>
      debounce((value) => {
        if (value && open) {
          setSearchQuery(value);
        } else {
          setSearchQuery("");
        }
      }, 300),
    [open]
  );

  useEffect(() => {
    return () => {
      debouncedSetSearchQuery.clear();
    };
  }, [debouncedSetSearchQuery]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const searchContainer = document.querySelector(".search-container");
      if (searchContainer && !searchContainer.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  // Add scroll lock effect
  useEffect(() => {
    const searchContainer = document.querySelector(".search-container");
    if (searchContainer) {
      searchContainer.style.overflow = "hidden";
    }
    return () => {
      if (searchContainer) {
        searchContainer.style.overflow = "auto";
      }
    };
  }, []);

  // Debug logging
  useEffect(() => {
    console.log("Recent pairs:", recentPairs);
    console.log("Search query:", searchQuery);
  }, [recentPairs, searchQuery]);

  const handleInputChange = (event) => {
    const value = event.target.value;
    setInputValue(value);
    debouncedSetSearchQuery(value);
  };

  const handlePairClick = (pair) => {
    // Clear search state first
    setInputValue("");
    setSearchQuery("");
    // Force a re-execution with empty query to clear results
    reexecuteSearch({ requestPolicy: "network-only" });

    // Update recent pairs
    setRecentPairs((prev) => {
      const newPairs = [
        pair,
        ...prev.filter((p) => p.address !== pair.address),
      ];
      return newPairs.slice(0, 5);
    });

    // Close search
    onClose();

    // Navigate to the new pair
    navigate(`?pair=${pair.address}`, { replace: true });
  };

  // Debug logging for recent pairs changes
  useEffect(() => {
    console.log("Recent pairs updated:", recentPairs);
  }, [recentPairs]);

  return (
    <Fade in timeout={150}>
      <Box
        className="search-container"
        sx={{
          position: "fixed",
          top: 0,
          left: { xs: 0, md: `${COLLAPSED_DRAWER_WIDTH}px` },
          right: 0,
          bottom: 0,
          zIndex: 1300,
          bgcolor: "background.default",
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: "background.paper",
            boxShadow: theme.shadows[1],
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 600,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <TextField
              autoFocus
              fullWidth
              placeholder="Search by token name, symbol, or address..."
              variant="outlined"
              size="small"
              value={inputValue}
              onChange={handleInputChange}
              InputProps={{
                sx: {
                  fontSize: "1rem",
                },
                startAdornment: (
                  <SearchIcon
                    sx={{
                      ml: 1,
                      mr: 2,
                      fontSize: 20,
                      color: "text.secondary",
                    }}
                  />
                ),
              }}
            />
            <IconButton
              onClick={onClose}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: "text.primary",
                  bgcolor: alpha(theme.palette.text.primary, 0.04),
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 600,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* Recent Pairs Section - Always visible when there are recent pairs */}
            {recentPairs.length > 0 && (
              <Box sx={{ width: "100%" }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Recent Pairs
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "nowrap",
                    gap: 1,
                    overflowX: "auto",
                    scrollbarWidth: "thin",
                    "&::-webkit-scrollbar": {
                      height: 4,
                    },
                    "&::-webkit-scrollbar-track": {
                      background: theme.palette.divider,
                      borderRadius: 2,
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: theme.palette.primary.main,
                      borderRadius: 2,
                    },
                    pb: 1,
                  }}
                >
                  {recentPairs.map((pair) => (
                    <Chip
                      key={pair.address}
                      onClick={() => handlePairClick(pair)}
                      label={`${pair.token.symbol}/${pair.baseToken.symbol}`}
                      sx={{
                        cursor: "pointer",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Search Results */}
            {searchQuery && (
              <Slide direction="up" in timeout={200}>
                <Box>
                  {searchResults.fetching ? (
                    <>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Searching...
                      </Typography>
                      <Paper
                        elevation={0}
                        sx={{
                          bgcolor: "background.paper",
                          borderRadius: 1,
                          overflow: "hidden",
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        {[...Array(8)].map((_, index) => (
                          <Box
                            key={index}
                            sx={{
                              p: 2,
                              opacity: 1 - index * 0.1,
                              "&:not(:last-child)": {
                                borderBottom: `1px solid ${theme.palette.divider}`,
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Skeleton
                                variant="circular"
                                width={40}
                                height={40}
                              />
                              <Box sx={{ flex: 1 }}>
                                <Skeleton
                                  variant="text"
                                  width="60%"
                                  height={24}
                                />
                                <Skeleton
                                  variant="text"
                                  width="40%"
                                  height={20}
                                />
                                <Skeleton
                                  variant="text"
                                  width="80%"
                                  height={16}
                                />
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Paper>
                    </>
                  ) : searchResults.data?.pairs?.length > 0 ? (
                    <>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {searchResults.data.pairs.length} results found
                      </Typography>
                      <Paper
                        elevation={0}
                        sx={{
                          bgcolor: "background.paper",
                          borderRadius: 1,
                          overflow: "hidden",
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        {searchResults.data.pairs.map((pair) => {
                          const priceChange24h =
                            ((pair?.priceUSD - pair?.priceUSD24h) /
                              pair?.priceUSD24h) *
                            100;
                          return (
                            <Box
                              key={pair.address}
                              onClick={() => handlePairClick(pair)}
                              sx={{
                                p: 2,
                                cursor: "pointer",
                                transition: "all 0.2s ease-in-out",
                                "&:hover": {
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.04
                                  ),
                                },
                                "&:not(:last-child)": {
                                  borderBottom: `1px solid ${theme.palette.divider}`,
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                }}
                              >
                                <Avatar
                                  token={pair.token}
                                  size={50}
                                  fontSize={10}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      mb: 0.5,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: 500,
                                      }}
                                    >
                                      {pair.token.name}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={`${pair.token.symbol}/${pair.baseToken.symbol}`}
                                      sx={{
                                        height: 20,
                                        fontSize: "0.75rem",
                                        bgcolor: alpha(
                                          theme.palette.primary.main,
                                          0.1
                                        ),
                                        color: theme.palette.primary.main,
                                      }}
                                    />
                                    {pair.pairFactory && (
                                      <Box
                                        sx={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                          ml: 0.5,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: 16,
                                            height: 16,
                                            borderRadius: "50%",
                                            overflow: "hidden",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: "background.paper",
                                            border: "1px solid",
                                            borderColor: "divider",
                                          }}
                                        >
                                          {pair.pairFactory.logoURL ? (
                                            <img
                                              src={pair.pairFactory.logoURL}
                                              alt={pair.pairFactory.name}
                                              style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                              }}
                                            />
                                          ) : (
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                fontSize: "0.5rem",
                                                fontWeight: 500,
                                                color: "text.secondary",
                                              }}
                                            >
                                              {pair.pairFactory.name.charAt(0)}
                                            </Typography>
                                          )}
                                        </Box>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: "text.secondary",
                                            fontSize: "0.75rem",
                                          }}
                                        >
                                          {pair.pairFactory.name}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontFamily: "monospace",
                                      color: "text.secondary",
                                      display: "block",
                                    }}
                                  >
                                    {pair.address}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1,
                                  mt: 1,
                                }}
                              >
                                <Tooltip title="24h Volume">
                                  <Chip
                                    size="small"
                                    label={`Vol: $${formatNumberHumanize(pair.volume24h)}`}
                                    sx={{
                                      height: 20,
                                      fontSize: "0.75rem",
                                      bgcolor: alpha(
                                        theme.palette.success.main,
                                        0.1
                                      ),
                                      color: theme.palette.success.main,
                                    }}
                                  />
                                </Tooltip>
                                <Tooltip title="Liquidity">
                                  <Chip
                                    size="small"
                                    label={`Liq: $${formatNumberHumanize(pair.liquidity)}`}
                                    sx={{
                                      height: 20,
                                      fontSize: "0.75rem",
                                      bgcolor: alpha(
                                        theme.palette.info.main,
                                        0.1
                                      ),
                                      color: theme.palette.info.main,
                                    }}
                                  />
                                </Tooltip>
                                <Tooltip title="Price">
                                  <Chip
                                    size="small"
                                    label={`Price: $${formatNumberHumanize(pair.priceUSD)}`}
                                    sx={{
                                      height: 20,
                                      fontSize: "0.75rem",
                                      bgcolor: alpha(
                                        theme.palette.warning.main,
                                        0.1
                                      ),
                                      color: theme.palette.warning.main,
                                    }}
                                  />
                                </Tooltip>
                                <Tooltip title="24h Change">
                                  <Chip
                                    size="small"
                                    label={`24h: ${formatNumberHumanize(
                                      pair.priceUSD24h ? priceChange24h : 0,
                                      { decimals: 2 }
                                    )}%`}
                                    sx={{
                                      height: 20,
                                      fontSize: "0.75rem",
                                      bgcolor: alpha(
                                        pair.priceUSD24h && priceChange24h >= 0
                                          ? theme.palette.success.main
                                          : theme.palette.error.main,
                                        0.1
                                      ),
                                      color:
                                        pair.priceUSD24h && priceChange24h >= 0
                                          ? theme.palette.success.main
                                          : theme.palette.error.main,
                                    }}
                                  />
                                </Tooltip>
                                {(pair.buyTax !== null ||
                                  pair.sellTax !== null) && (
                                  <Tooltip title="Buy/Sell Tax">
                                    <Chip
                                      size="small"
                                      label={
                                        <Box
                                          sx={{
                                            display: "inline-flex",
                                            gap: 0.5,
                                          }}
                                        >
                                          <Typography
                                            component="span"
                                            sx={{
                                              color: "success.main",
                                              fontSize: "0.75rem",
                                            }}
                                          >
                                            {pair.buyTax ?? "?"}%
                                          </Typography>
                                          <Typography
                                            component="span"
                                            sx={{ fontSize: "0.75rem" }}
                                          >
                                            /
                                          </Typography>
                                          <Typography
                                            component="span"
                                            sx={{
                                              color: "error.main",
                                              fontSize: "0.75rem",
                                            }}
                                          >
                                            {pair.sellTax ?? "?"}%
                                          </Typography>
                                        </Box>
                                      }
                                      sx={{
                                        height: 20,
                                        fontSize: "0.75rem",
                                        bgcolor: alpha(
                                          pair.buyTax > 10 || pair.sellTax > 10
                                            ? theme.palette.warning.main
                                            : theme.palette.primary.main,
                                          0.1
                                        ),
                                        color:
                                          pair.buyTax > 10 || pair.sellTax > 10
                                            ? theme.palette.warning.main
                                            : theme.palette.primary.main,
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                      </Paper>
                    </>
                  ) : (
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ textAlign: "center" }}
                    >
                      No results found
                    </Typography>
                  )}
                </Box>
              </Slide>
            )}
          </Box>
        </Box>
      </Box>
    </Fade>
  );
};

export default FullScreenSearch;
